import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, UploadCloud, FileText, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const DOC_CATEGORIES = ["Technical Guide", "White Paper", "Application Guide", "Educational", "Product Literature", "Research / Reference"];
const EMPTY_FORM = { title: "", description: "", category: "Technical Guide", author: "", tags: "", related_products: "", status: "published" };

export default function DocumentManager() {
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fileName, setFileName] = useState("");
  const [thumbName, setThumbName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const fileRef = useRef(null);
  const thumbRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    e.preventDefault();
    if (!fileRef.current?.files?.[0]) { toast.error("Select a file to upload"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", fileRef.current.files[0]);
      if (thumbRef.current?.files?.[0]) fd.append("thumbnail", thumbRef.current.files[0]);
      await api.post("/admin/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document published to Knowledge Center");
      setForm(EMPTY_FORM);
      setFileName("");
      setThumbName("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleStatus = async (doc) => {
    try {
      await api.put(`/admin/documents/${doc.id}`, { status: doc.status === "published" ? "draft" : "published" });
      load();
    } catch { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try { await api.delete(`/admin/documents/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const startReplace = (id) => { setReplaceId(id); replaceRef.current?.click(); };

  const doReplace = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !replaceId) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/documents/${replaceId}/replace-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced — title, description and links kept as they were");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplaceId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div data-testid="document-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink">Knowledge Center Documents</h2>
        <button onClick={() => setShowForm(!showForm)} data-testid="doc-upload-toggle"
          className="inline-flex items-center gap-1.5 bg-accent px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent-dark">
          <Plus size={14} /> Upload Document
        </button>
      </div>
      <p className="mt-2 text-sm text-steel">
        Upload PDFs, technical notes, white papers and educational material. Only documents marked published appear publicly in the Knowledge Center. Never upload confidential drawings, customer data or internal business documents here.
      </p>

      {showForm && (
        <form onSubmit={upload} className="mt-6 border border-line bg-white p-6" data-testid="doc-upload-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="doc-title" className="label-field">Title *</label>
              <input id="doc-title" data-testid="doc-title-input" required className="input-field" value={form.title} onChange={set("title")} />
            </div>
            <div>
              <label htmlFor="doc-category" className="label-field">Category</label>
              <select id="doc-category" data-testid="doc-category-select" className="input-field" value={form.category} onChange={set("category")}>
                {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="doc-desc" className="label-field">Short Description</label>
              <textarea id="doc-desc" data-testid="doc-desc-input" rows={2} className="input-field" value={form.description} onChange={set("description")} />
            </div>
            <div>
              <label htmlFor="doc-author" className="label-field">Author / Organization</label>
              <input id="doc-author" data-testid="doc-author-input" className="input-field" value={form.author} onChange={set("author")} placeholder="STJ Engineering Desk" />
            </div>
            <div>
              <label htmlFor="doc-tags" className="label-field">Tags (comma separated)</label>
              <input id="doc-tags" data-testid="doc-tags-input" className="input-field" value={form.tags} onChange={set("tags")} placeholder="cleanroom, pass box" />
            </div>
            <div>
              <label htmlFor="doc-related" className="label-field">Related Product Slugs (comma separated)</label>
              <input id="doc-related" data-testid="doc-related-input" className="input-field" value={form.related_products} onChange={set("related_products")} placeholder="dynamic-pass-box, static-pass-box" />
            </div>
            <div>
              <label className="label-field" htmlFor="doc-thumb">Thumbnail (optional image)</label>
              <label htmlFor="doc-thumb" data-testid="doc-thumb-label"
                className="flex cursor-pointer items-center gap-3 border border-dashed border-line bg-mist p-3.5 text-sm text-steel hover:border-accent/50">
                <UploadCloud size={17} className="text-accent" />
                {thumbName || "Choose image…"}
                <input id="doc-thumb" ref={thumbRef} type="file" className="sr-only" accept=".jpg,.jpeg,.png"
                  onChange={(e) => setThumbName(e.target.files?.[0]?.name || "")} />
              </label>
            </div>
            <div>
              <label htmlFor="doc-status" className="label-field">Visibility</label>
              <select id="doc-status" data-testid="doc-status-select" className="input-field" value={form.status} onChange={set("status")}>
                <option value="published">Published (public)</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
            <div>
              <label className="label-field" htmlFor="doc-file">File * (PDF, DOC, DOCX, PNG, JPG — max 10 MB)</label>
              <label htmlFor="doc-file" data-testid="doc-file-label"
                className="flex cursor-pointer items-center gap-3 border border-dashed border-line bg-mist p-3.5 text-sm text-steel hover:border-accent/50">
                <UploadCloud size={17} className="text-accent" />
                {fileName || "Choose file…"}
                <input id="doc-file" ref={fileRef} type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
              </label>
            </div>
          </div>
          <button type="submit" data-testid="doc-upload-submit" disabled={uploading}
            className="mt-5 bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60">
            {uploading ? "Uploading…" : "Publish Document"}
          </button>
        </form>
      )}

      <div className="mt-6 overflow-x-auto border border-line bg-white">
        <input ref={replaceRef} type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          data-testid="doc-replace-file-input" onChange={doReplace} />
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Document</th>
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Category</th>
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Date</th>
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Status</th>
              <th className="px-5 py-3.5 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-line/60 last:border-0" data-testid={`doc-row-${d.id}`}>
                <td className="px-5 py-3.5">
                  <span className="flex items-center gap-2.5 font-medium text-ink">
                    <FileText size={14} className="shrink-0 text-accent" /> {d.title}
                    {d.file_url && (
                      <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                        data-testid={`doc-view-${d.id}`} className="text-steel hover:text-accent" aria-label="View file">
                        <ExternalLink size={13} />
                      </a>
                    )}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-steel">{d.category}</td>
                <td className="px-5 py-3.5 text-steel">{fmtDate(d.created_at)}</td>
                <td className="px-5 py-3.5">
                  <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${d.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-mist text-steel"}`}>{d.status}</span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => startReplace(d.id)} data-testid={`doc-replace-${d.id}`}
                    className="mr-2 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-steel hover:border-accent hover:text-accent">
                    Replace File
                  </button>
                  <button onClick={() => toggleStatus(d)} data-testid={`doc-toggle-${d.id}`}
                    className="mr-2 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-steel hover:border-accent hover:text-accent">
                    {d.status === "published" ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} aria-label="Delete"
                    className="inline-flex h-8 w-8 items-center justify-center border border-line text-steel hover:border-red-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!docs.length && <p className="p-8 text-center text-sm text-steel">No documents uploaded yet.</p>}
      </div>
    </div>
  );
}
